/**
 * My payslip — the self-service half of `/payroll`, WP-W9.
 * `web/shapes/payroll.md` §5 is the agreed shape.
 *
 * The one payroll surface every employee reaches, not only an administrator:
 * `src/lib/hiring-permissions.ts`'s `SELF_SERVICE_FLOOR` gives every role
 * `payroll.view_own_payslip` before any grant row, so `nav/menu.ts` declares the
 * payroll item with no role check at all.
 *
 * **SELF-ONLY, structurally.** This panel calls `payrollMyPayslipPath(periodId)`
 * (`api/payroll.ts`), whose only parameter is a period id — there is no employee id
 * anywhere in this file, no prop that could carry one, and no form field that could
 * type one. The server resolves the subject from the session
 * (`src/modules/payroll/service.ts`'s `myPayslip`); this panel simply has nothing to
 * send that could name anyone else.
 *
 * `GET /payroll/periods` is gated on `payroll.view_payroll`, a code the self-service
 * floor does not carry, so for a plain employee there is genuinely no period
 * *directory* this form can offer — a permission mismatch, not a missed wiring,
 * unlike the employee/cycle-id case `hr-leave.md` and `hr-performance.md` describe,
 * where the picker's own endpoint is readable by every session that reaches the
 * typed field.
 *
 * **But `PayrollScreen` reads that list anyway**, for `RunsPanel`, and a session that
 * holds `payroll.view_payroll` is looking at the answer two sections further down
 * while this one asks it to type the id from memory (fix round 2, R2). So the same
 * `periods` array now reaches this panel and the field becomes the same `<select>`
 * the runs filter already uses — `null` (in flight, or refused, which is the
 * employee's ordinary case) keeps the typed box, the identical honest fallback
 * `RunsPanel` itself carries.
 *
 * **The PDF (roadmap U26).** Once a payslip is released the panel offers it as a PDF,
 * `GET /payroll/my-payslip.pdf?period_id=` — a sensitive read, so the download waits on
 * an access reason (`PayslipReasonField`), even for one's own payslip. `PayrollScreen`
 * builds the path and the file name from the period id alone; this file still holds no
 * employee id.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { PayrollComponents, PayrollItem, PayrollPeriod } from '../../api/payroll';
import {
  formatMoney,
  formatPayrollComponent,
  PAYROLL_COMPONENT_LABEL,
  PAYROLL_COMPONENT_ORDER,
} from '../../payroll/status';
import { useCompanyCurrency } from '../../api/company-settings';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { isUsableReason, MIN_ACCESS_REASON } from '../../access/reason';
import { myPayslipDownloadKey, type PayslipDownloadState } from '../../payroll/payslip-download';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Icon } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { PayslipReasonField } from './PayslipReasonField';

export interface MyPayslipPanelProps {
  readonly periodId: string | null;
  readonly onLoadPeriod: (periodId: string) => void;
  /**
   * `GET /payroll/periods`, already read by the screen for `RunsPanel`. `null` while
   * it is in flight and `null` when it was refused — both mean "no list to offer",
   * and the field stays a typed box.
   */
  readonly periods: readonly PayrollPeriod[] | null;

  readonly status: QueryStatus;
  /** The `data` half of `{ data: PayrollItem | null }` — `null` is a real, non-error answer. */
  readonly payslip: PayrollItem | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;

  /** The last payslip-PDF download, whoever's; this panel shows only its own (`'my'`). */
  readonly pdfDownload: PayslipDownloadState;
  readonly onDownloadPdf: (reason: string) => void;
}

export function MyPayslipPanel(props: MyPayslipPanelProps): ReactNode {
  const { t } = useI18n();
  const [periodIdInput, setPeriodIdInput] = useState('');
  const [pdfReason, setPdfReason] = useState('');

  return (
    <section className="wp-section" aria-labelledby="w9-my-heading" data-testid="w9-my">
      <h2 className="wp-section__title" id="w9-my-heading">
        {t('payroll.my.title')}
      </h2>
      <p className="wp-small wp-muted">{t('payroll.my.intro')}</p>

      <form
        className="wp-filters wp-filters--bar"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = periodIdInput.trim();
          if (trimmed === '') return;
          props.onLoadPeriod(trimmed);
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w9-my-period-id">
            {props.periods === null
              ? t('payroll.periodIdLabel')
              : t('payroll.periods.pickLabel')}
          </label>
          {props.periods === null ? (
            <input
              className="wp-field__input"
              id="w9-my-period-id"
              data-testid="w9-my-period-id"
              type="text"
              inputMode="numeric"
              value={periodIdInput}
              autoComplete="off"
              onChange={(event) => {
                setPeriodIdInput(event.target.value);
              }}
            />
          ) : (
            <div className="wp-select">
              <select
                className="wp-field__input"
                id="w9-my-period-id"
                data-testid="w9-my-period-id"
                value={periodIdInput}
                onChange={(event) => {
                  setPeriodIdInput(event.target.value);
                }}
              >
                <option value="">{t('payroll.periods.choose')}</option>
                {props.periods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.label}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down" className="wp-select__chevron" />
            </div>
          )}
          {props.periods === null ? (
            <p className="wp-small wp-muted">{t('payroll.periodIdHint')}</p>
          ) : null}
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w9-my-load"
            disabled={periodIdInput.trim() === ''}
          >
            {t('payroll.my.load')}
          </button>
        </div>
      </form>

      {props.periodId === null ? (
        <EmptyState
          title={t('payroll.my.none.title')}
          description={t('payroll.my.none.body')}
          testId="w9-my-none"
        />
      ) : (
        <>
          {props.status === 'loading' ? (
            <Skeleton
              rows={3}
              variant="block"
              label={t('payroll.my.subject')}
              testId="w9-my-skeleton"
            />
          ) : null}
          {props.status === 'error' && props.error !== null ? (
            <ApiErrorNote
              error={props.error}
              onRetry={props.onReload}
              subject={t('payroll.my.subject')}
              testId="w9-my-error"
            />
          ) : null}
          {props.status === 'ok' && props.payslip === null ? (
            <EmptyState
              title={t('payroll.my.empty.title')}
              description={t('payroll.my.empty.body')}
              testId="w9-my-empty"
            />
          ) : null}
          {props.status === 'ok' && props.payslip !== null ? (
            <>
              <PayslipBreakdown payslip={props.payslip} />
              <PayslipPdf
                reason={pdfReason}
                onReason={setPdfReason}
                download={props.pdfDownload}
                periodId={props.periodId}
                onDownload={() => {
                  props.onDownloadPdf(pdfReason);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Which lines close a group, which line closes the sheet, which are not money at all,
 * and which took money *off* the employee.
 *
 * All four are read off `src/modules/payroll/engine.ts`'s own arithmetic, not guessed
 * from the label: `tong_*` are the engine's own sums, `gio_*`/`ty_le_dat` are hours and
 * a ratio that the sheet refers to but does not add, and the outgoing set is exactly
 * the employee-side lines — the employer-side insurance (`*_cty`) is the company's
 * money and is deliberately NOT coloured, because colouring it would tell an employee
 * that 21.5% of their payroll cost came out of their pay (Luật Màu Có Nghĩa).
 */
const LEDGER_SUBTOTAL: ReadonlySet<keyof PayrollComponents> = new Set([
  'luong_chinh',
  'luong',
  'tong_phu_cap',
  'tong_bhnv',
  'tong_bh_cty',
  'tong_bh_kpcd',
  'tong_luong',
  'tong_chi_tra',
]);

const LEDGER_TOTAL: keyof PayrollComponents = 'thuc_lanh';

const LEDGER_MEASURE: ReadonlySet<keyof PayrollComponents> = new Set([
  'gio_cong',
  'ty_le_dat',
  'gio_ot',
]);

const LEDGER_OUTGOING: ReadonlySet<keyof PayrollComponents> = new Set([
  'giam_thuong',
  'khau_tru_boi_thuong',
  'bhxh_nv',
  'bhyt_nv',
  'kpcd_nv',
  'tong_bhnv',
  'tam_ung',
]);

function ledgerLineClass(key: keyof PayrollComponents): string {
  if (key === LEDGER_TOTAL) return 'wp-ledger__line--total';
  if (LEDGER_SUBTOTAL.has(key)) return 'wp-ledger__line--subtotal';
  if (LEDGER_MEASURE.has(key)) return 'wp-ledger__line--measure';
  return '';
}

/**
 * The per-component breakdown, `explanation.components`, in `PAYROLL_COMPONENT_ORDER`,
 * rendered as the ledger it is: one ruled line per component, the name on the left, the
 * money right-aligned in tabular figures, a heavier rule under each sub-total and the
 * heaviest under Thực lãnh. It was a list of `.wp-row` cards, which put every figure in
 * its own box and made two numbers in the same column impossible to compare — the exact
 * failure `.wp-num` and Luật Số Thẳng Cột exist to prevent.
 */
function PayslipBreakdown({ payslip }: { readonly payslip: PayrollItem }): ReactNode {
  const { t } = useI18n();
  // The company's own unit, not this bundle's guess. The column head carries it once
  // so the twenty-nine figures under it stay comparable down the page.
  const currency = useCompanyCurrency();
  const components = payslip.explanation.components;
  // `khau_tru_toi_da` is a CEILING, not money that moved (fix round 1 C2): it is
  // printed as its own note, beside the deduction it bounds, never as an additive row
  // in `w9-my-components` — an employee adding up that list's own figures must be able
  // to reconcile them to net pay. Shown only when a `capped` ground's deduction
  // actually landed on this payslip; `khau_tru_toi_da` is otherwise `0` (`engine.ts`).
  const ceiling = components.khau_tru_toi_da;
  return (
    <div data-testid={`w9-my-payslip-${payslip.id}`}>
      <div className="wp-table-scroll">
        <table className="wp-table wp-table--fluid wp-ledger" data-testid="w9-my-components">
          <caption className="wp-visually-hidden">
            {t('payroll.my.ledger.caption', { currency })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('payroll.my.ledger.col.line')}</th>
              <th scope="col" className="wp-ledger__amount">
                {t('payroll.my.ledger.col.amount', { currency })}
              </th>
            </tr>
          </thead>
          <tbody>
            {PAYROLL_COMPONENT_ORDER.map((key) => (
              <tr key={key} className={ledgerLineClass(key)} data-testid={`w9-my-component-${key}`}>
                <th scope="row">{t(PAYROLL_COMPONENT_LABEL[key])}</th>
                <td
                  className={
                    LEDGER_OUTGOING.has(key)
                      ? 'wp-ledger__amount wp-money--out'
                      : 'wp-ledger__amount'
                  }
                >
                  {/* Not every component is money — QA-G-032, `PAYROLL_COMPONENT_UNIT`. */}
                  {formatPayrollComponent(key, components[key], currency)}
                </td>
              </tr>
            ))}
          </tbody>
          {/* The foot states net pay in words — the one sentence on a sheet of figures,
              and the line the employee is actually here for. */}
          <tfoot>
            <tr>
              <td colSpan={2} data-testid="w9-my-net">
                {t('payroll.my.net', { value: formatMoney(Number(payslip.net), currency) })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {ceiling <= 0 ? null : (
        <p className="wp-small wp-muted" data-testid="w9-my-deduction-ceiling">
          {t('payroll.my.deductionCeiling', { value: formatMoney(ceiling, currency) })}
        </p>
      )}
    </div>
  );
}

/** My payslip as a PDF — only for a released payslip, and only on a usable reason. */
function PayslipPdf(props: {
  readonly reason: string;
  readonly onReason: (value: string) => void;
  readonly download: PayslipDownloadState;
  readonly onDownload: () => void;
  /**
   * R11: the download this panel sent is keyed `my:<periodId>` (`myPayslipDownloadKey`),
   * so switching the typed period never shows a PDF outcome that belongs to the payslip
   * that used to be on screen. Required, not `string | null`: the caller only renders
   * this component once a period is actually loaded (fix round 1) — no `?? ''` fallback
   * here to silently paper over the two sides disagreeing on what "no period" means.
   */
  readonly periodId: string;
}): ReactNode {
  const { t } = useI18n();
  const usable = isUsableReason(props.reason);
  const requesting = props.download.status === 'requesting';
  const myKey = myPayslipDownloadKey(props.periodId);
  const mine = props.download.status !== 'idle' && props.download.key === myKey;
  return (
    <div className="wp-section__subsection" data-testid="w9-my-pdf-block">
      <PayslipReasonField
        value={props.reason}
        onChange={props.onReason}
        testId="w9-my-pdf-reason"
      />
      <div className="wp-filters__actions">
        {/* Disabled, not absent: the one honest disabled control — `access/reason.ts`. */}
        <button
          className="wp-button"
          type="button"
          data-testid="w9-my-pdf"
          disabled={!usable || requesting}
          onClick={props.onDownload}
        >
          {mine && requesting ? t('payroll.pdf.downloading') : t('payroll.pdf.download')}
        </button>
        {usable ? null : (
          <p className="wp-small wp-muted" data-testid="w9-my-pdf-reason-needed">
            {t('payroll.pdf.reasonNeeded', { min: MIN_ACCESS_REASON })}
          </p>
        )}
      </div>
      {mine && props.download.status === 'refused' ? (
        <ApiErrorNote
          error={props.download.error}
          subject={t('payroll.pdf.subject')}
          unavailableNote={t('payroll.pdf.notFound')}
          testId="w9-my-pdf-error"
        />
      ) : null}
      {mine && props.download.status === 'delivered' ? (
        <StatusNote
          severity="low"
          title={t('payroll.pdf.delivered', { filename: props.download.filename })}
          testId="w9-my-pdf-ok"
        />
      ) : null}
    </div>
  );
}
